import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Dashboard from './components/Dashboard';
import ServiceAccess from './components/ServiceAccess';
import AuthLanding from './components/AuthLanding';
import './App.css';
import './components/LoginSignUp.css';

// Login/Signup Component with tabs
const LoginSignUp = () => {
  const [activeTab, setActiveTab] = React.useState('login'); // 'login', 'signup', 'reset', 'confirmReset', 'newPassword'
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [resetCode, setResetCode] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [message, setMessage] = React.useState('');
  const { signUp, signIn, signInWithEndava, authError, endavaSsoAvailable, completeNewPassword, user, signOut, resetPassword, confirmResetPassword } = useAuth();

  // Function to handle tab switching and clear message/fields
  const handleTabSwitch = (tabName) => {
    const currentTab = activeTab;
    
    // Clear message when switching tabs
    setMessage('');
    
    // Field clearing logic
    if (currentTab === 'login' && (tabName === 'signup' || tabName === 'reset')) {
      // From login to signup/reset: clear ALL fields
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setResetCode('');
      setNewPassword('');
    } else if (tabName === 'login' && (currentTab === 'signup' || currentTab === 'reset' || currentTab === 'confirmReset')) {
      // From any other tab back to login: keep only email, clear others
      // Keep email as is
      setPassword('');
      setConfirmPassword('');
      setResetCode('');
      setNewPassword('');
    }
    
    setActiveTab(tabName);
  };

  // Debug: If user is already logged in, show option to sign out
  if (user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px' 
      }}>
        <div style={{ padding: '20px', maxWidth: '400px', textAlign: 'center' }}>
          <h2>Already Logged In</h2>
          <p>You are logged in as: <strong>{user.email}</strong></p>
          <button
            onClick={async () => {
              await signOut();
              window.location.reload();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            Sign Out & Reload
          </button>
          <button
            onClick={() => window.location.href = '/dashboard'}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('Signing in...');
    
    try {
      const result = await signIn(email, password);
      if (result.success) {
        if (result.requiresNewPassword) {
          setPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setActiveTab('newPassword');
          setMessage('Choose a permanent password to finish signing in.');
          return;
        }
        setMessage('Login successful! Redirecting...');
        // Force redirect to dashboard
        setTimeout(() => {
          window.location.href = window.location.pathname === '/service-access' ? window.location.pathname + window.location.search : '/dashboard';
        }, 1000);
      } else {
        setMessage('Login failed: ' + result.error);
      }
    } catch (error) {
      setMessage('Login error: ' + error.message);
      console.error('Login error:', error);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    const result = await completeNewPassword(newPassword);
    if (result.success) {
      setMessage('Password updated! Redirecting...');
      setTimeout(() => {
        window.location.href = window.location.pathname === '/service-access' ? window.location.pathname + window.location.search : '/dashboard';
      }, 500);
    } else {
      setMessage('Could not set your new password: ' + result.error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage('');
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    
    const result = await signUp(email, password);
    if (result.success) {
      setMessage('Account created! Check your email for confirmation code.');
      setTimeout(() => {
        window.location.href = '/confirm?email=' + encodeURIComponent(email);
      }, 2000);
    } else {
      setMessage('Signup failed: ' + result.error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    
    if (resetPassword) {
      const result = await resetPassword(email);
      if (result.success) {
        setMessage('Verification code sent to your email. Please check your inbox.');
        setActiveTab('confirmReset');
      } else {
        setMessage('Reset failed: ' + result.error);
      }
    } else {
      setMessage('Reset password functionality not available.');
    }
  };

  const handleConfirmResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    
    if (confirmResetPassword) {
      const result = await confirmResetPassword(email, resetCode, newPassword);
      if (result.success) {
        setMessage('Password reset successful! You can now login with your new password.');
        setActiveTab('login');
        // Clear form
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage('Reset confirmation failed: ' + result.error);
      }
    }
  };

  return (
    <div className="center-wrapper">
      <div className="login-signup-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('login')}
          >
            Login
          </button>
          <button
            className={`tab ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('signup')}
          >
            Sign Up
          </button>
          <button
            className={`tab ${activeTab === 'reset' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('reset')}
          >
            Reset Password
          </button>
        </div>

        <div className="form-container">
          <h2>
            {activeTab === 'login' && 'Login'}
            {activeTab === 'signup' && 'Sign Up'}
            {activeTab === 'reset' && 'Reset Password'}
            {activeTab === 'confirmReset' && 'Confirm Reset'}
            {activeTab === 'newPassword' && 'Choose a New Password'}
          </h2>
          
          {(message || authError) && (
            <div className="message">
              {message || authError}
            </div>
          )}

          {activeTab === 'login' && endavaSsoAvailable && <button type="button" onClick={async () => { const result = await signInWithEndava(); if (!result.success) setMessage(result.error); }}>Sign in with Endava</button>}
          <form onSubmit={
            activeTab === 'login' ? handleLogin : 
            activeTab === 'signup' ? handleSignUp : 
            activeTab === 'reset' ? handleResetPassword :
            activeTab === 'confirmReset' ? handleConfirmResetPassword :
            handleNewPassword
          }>
          {/* Email field - shown for all except confirmReset */}
          {activeTab !== 'confirmReset' && activeTab !== 'newPassword' && (
            <div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          )}
          
          {/* Password field - shown for login and signup */}
          {(activeTab === 'login' || activeTab === 'signup') && (
            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          )}
          
          {/* Confirm Password - shown for signup */}
          {activeTab === 'signup' && (
            <div>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          )}
          
          {/* Reset confirmation fields */}
          {activeTab === 'confirmReset' && (
            <>
              <div>
                <input
                  type="text"
                  placeholder="Enter 6-digit verification code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  maxLength="6"
                  required
                  style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    letterSpacing: '2px'
                  }}
                />
              </div>
              <div>
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {activeTab === 'newPassword' && (
            <>
              <p>Set a permanent password for <strong>{email}</strong>.</p>
              <div>
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </>
          )}
          
            <button type="submit">
              {activeTab === 'login' && 'Login'}
              {activeTab === 'signup' && 'Create Account'}
              {activeTab === 'reset' && 'Send Reset Code'}
              {activeTab === 'confirmReset' && 'Reset Password'}
              {activeTab === 'newPassword' && 'Set New Password'}
            </button>
            
            {/* Back button for confirmReset */}
            {activeTab === 'confirmReset' && (
              <button
                type="button"
                onClick={() => handleTabSwitch('reset')}
              >
                Back to Reset
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

// Email Confirmation Component
const ConfirmSignUp = () => {
  const [code, setCode] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const { confirmSignUp } = useAuth();

  React.useEffect(() => {
    // Get email from URL params
    const params = new URLSearchParams(window.location.search);
    setEmail(params.get('email') || '');
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setMessage('');
    
    const result = await confirmSignUp(email, code);
    if (result.success) {
      setMessage('Account confirmed! You can now login.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } else {
      setMessage('Confirmation failed: ' + result.error);
    }
  };

  const resendCode = () => {
    setMessage('New confirmation code sent to your email!');
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      padding: '20px' 
    }}>
      <div style={{ 
        maxWidth: '400px',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        <h2>Confirm Your Email</h2>
      <p>We sent a confirmation code to <strong>{email}</strong></p>
      
      {message && (
        <div style={{
          padding: '10px',
          marginBottom: '15px',
          backgroundColor: message.includes('Invalid') ? '#f8d7da' : '#d4edda',
          border: `1px solid ${message.includes('Invalid') ? '#f5c6cb' : '#c3e6cb'}`,
          borderRadius: '4px',
          color: message.includes('Invalid') ? '#721c24' : '#155724'
        }}>
          {message}
        </div>
      )}

      <form onSubmit={handleConfirm}>
        <div style={{ marginBottom: '15px' }}>
          <input
            type="text"
            placeholder="Enter 6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength="6"
            required
            style={{
              width: 'calc(100% - 20px)',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              textAlign: 'center',
              fontSize: '18px',
              letterSpacing: '2px',
              marginRight: '20px'
            }}
          />
        </div>
        <button
          type="submit"
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '10px'
          }}
        >
          Confirm Account
        </button>
      </form>
      
      <button
        onClick={resendCode}
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Resend Code
      </button>
      
        <p style={{ textAlign: 'center', marginTop: '15px', fontSize: '14px', color: '#666' }}>
          Check your email for the confirmation code
        </p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
  }
  
  return user ? children : <LoginSignUp />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/service-access" element={<ProtectedRoute><ServiceAccess /></ProtectedRoute>} />
            <Route path="/login" element={<LoginSignUp />} />
            <Route path="/confirm" element={<ConfirmSignUp />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<AuthLanding />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
